import { __assign, __read, __rest } from "tslib";
import React from "react";
import { BubbleContent } from "./BubbleContent";
import { isChildOfType } from "../_util/is-child-of-type";
import { Button } from "../button";
import { useScheduleUpdate } from "../_util/use-schedule-update";
import { Input } from "../input";
// 这里不直接 import Select 组件本身，避免形成
// bubble -> select -> list/VirtualizedList -> dropdown/Dropdown -> popover -> bubble
// 的循环依赖（在 ESM 运行环境下会导致 DropdownBox 读取为 undefined）。
// 判断原生 Select 组件仅通过 displayName 比较即可。
import { Popover } from "../popover";
import { TextArea } from "../input/TextArea";
// children 是否为 type 不为 'simulate' 的 Select 组件（即原生下拉）。
function isNativeSelectElement(child) {
    var _a, _b;
    if (!React.isValidElement(child)) {
        return false;
    }
    var displayName = (_a = child.type) === null || _a === void 0 ? void 0 : _a.displayName;
    return displayName === "Select" && ((_b = child.props) === null || _b === void 0 ? void 0 : _b.type) !== "simulate";
}
export var Bubble = React.forwardRef(function Bubble(_a, ref) {
    var visible = _a.visible, onVisibleChange = _a.onVisibleChange, content = _a.content, arrowPointAtCenter = _a.arrowPointAtCenter, _b = _a.trigger, trigger = _b === void 0 ? "hover" : _b, overlayClassName = _a.overlayClassName, overlayStyle = _a.overlayStyle, openDelay = _a.openDelay, closeDelay = _a.closeDelay, closeOnScroll = _a.closeOnScroll, updateOnDimensionChange = _a.updateOnDimensionChange, transitionTimeout = _a.transitionTimeout, escapeWithReference = _a.escapeWithReference, referenceElement = _a.referenceElement, popupContainer = _a.popupContainer, destroyOnClose = _a.destroyOnClose, _c = _a.placement, placement = _c === void 0 ? "top" : _c, placementOffset = _a.placementOffset, animationScaleFrom = _a.animationScaleFrom, onExited = _a.onExited, children = _a.children, _d = _a.updateDeps, updateDeps = _d === void 0 ? [content] : _d, flipped = _a.flipped, positionFixed = _a.positionFixed, modifiers = _a.modifiers, bubbleProps = __rest(_a, ["visible", "onVisibleChange", "content", "arrowPointAtCenter", "trigger", "overlayClassName", "overlayStyle", "openDelay", "closeDelay", "closeOnScroll", "updateOnDimensionChange", "transitionTimeout", "escapeWithReference", "referenceElement", "popupContainer", "destroyOnClose", "placement", "placementOffset", "animationScaleFrom", "onExited", "children", "updateDeps", "flipped", "positionFixed", "modifiers"]);
    // 内容变化时更新位置
    var scheduleUpdateRef = useScheduleUpdate(Array.isArray(updateDeps) ? updateDeps : [content]);
    // issue: https://github.com/facebook/react/issues/4251
    if (trigger !== "click" &&
        React.Children.count(children) === 1 &&
        (isChildOfType(children, "button") ||
            isChildOfType(children, "input") ||
            isChildOfType(children, "textarea") ||
            isChildOfType(children, "select") ||
            isChildOfType(children, Button) ||
            isChildOfType(children, Input) ||
            isChildOfType(children, TextArea) ||
            isNativeSelectElement(children)) &&
        children.props.disabled) {
        // eslint-disable-next-line no-param-reassign
        children = (React.createElement("span", { style: {
                // eslint-disable-next-line dot-notation
                display: children.props["size"] === "full" ? "block" : "inline-block",
                cursor: "not-allowed",
            } }, React.cloneElement(children, {
            style: __assign(__assign({}, (children.props.style || {})), { pointerEvents: "none" }),
        })));
    }
    var offset = placementOffset !== null && placementOffset !== void 0 ? placementOffset : 10;
    if (arrowPointAtCenter) {
        offset = Array.isArray(offset) ? offset[1] : offset;
        var _e = __read(placement.split("-"), 2), placementModifier = _e[1];
        if (placementModifier === "start") {
            offset = ["50%-26", offset];
        }
        else if (placementModifier === "end") {
            offset = ["26-50%", offset];
        }
    }
    return (React.createElement(Popover, { visible: visible, onVisibleChange: onVisibleChange, closeOnScroll: closeOnScroll, trigger: trigger, placement: placement, placementOffset: offset, flipped: flipped, positionFixed: positionFixed, animationScaleFrom: animationScaleFrom, referenceElement: referenceElement, overlay: function (_a) {
            var scheduleUpdate = _a.scheduleUpdate, placement = _a.placement;
            scheduleUpdateRef.current = scheduleUpdate;
            if (!content && content !== 0) {
                return null;
            }
            return (React.createElement(BubbleContent, __assign({ ref: ref }, bubbleProps, { placement: placement }), content));
        }, overlayClassName: overlayClassName, overlayStyle: overlayStyle, openDelay: openDelay, closeDelay: closeDelay, updateOnDimensionChange: updateOnDimensionChange, transitionTimeout: transitionTimeout, escapeWithReference: escapeWithReference, popupContainer: popupContainer, destroyOnClose: destroyOnClose, onExited: onExited, modifiers: modifiers }, children));
});
Bubble.displayName = "Bubble";
//# sourceMappingURL=Bubble.js.map